import { apiClient } from "../../../services/apiClient";
import {
  COUNT_FIELD_PREFIX,
  COUNT_TARGETS,
  rememberCountTargetLabels,
} from "./countCondition";

export type ConditionalCountOption = {
  code: string;
  name: string;
  field: string;
};

export type ConditionalCountOptions = {
  targets: ConditionalCountOption[];
  operators: string[];
};

function stripCountPrefix(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.startsWith(COUNT_FIELD_PREFIX)) return trimmed;
  return trimmed.slice(COUNT_FIELD_PREFIX.length).trim();
}

function labelForCode(code: string, explicit: string): string {
  const named = explicit.trim();
  if (named) return named;
  return COUNT_TARGETS.find((target) => target.code === code)?.label ?? code;
}

function asRecord(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  return payload as Record<string, unknown>;
}

function readOptionList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const record = asRecord(payload);
  if (!record) return [];
  for (const key of ["targets", "options", "items", "results", "data", "count_options"]) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return [];
}

function optionSource(payload: unknown): unknown {
  const record = asRecord(payload);
  if (!record) return payload;
  const nested = asRecord(record.data);
  if (
    nested &&
    (Array.isArray(nested.targets) || Array.isArray(nested.operators))
  ) {
    return nested;
  }
  return record;
}

function readOperators(payload: unknown): string[] {
  const record = asRecord(payload);
  if (!record || !Array.isArray(record.operators)) return [];
  const seen = new Set<string>();
  const operators: string[] = [];
  for (const item of record.operators) {
    const symbol = String(item ?? "").trim();
    if (!symbol || seen.has(symbol)) continue;
    seen.add(symbol);
    operators.push(symbol);
  }
  return operators;
}

/** Normalize GET `/auto-tagging/conditional-plans/count-options`. */
export function normalizeConditionalCountOptions(
  payload: unknown,
): ConditionalCountOptions {
  const source = optionSource(payload);
  const seen = new Set<string>();
  const targets: ConditionalCountOption[] = [];

  for (const item of readOptionList(source)) {
    let code = "";
    let name = "";
    if (typeof item === "string") {
      code = stripCountPrefix(item);
    } else if (item && typeof item === "object") {
      const record = item as Record<string, unknown>;
      code = stripCountPrefix(
        String(record.value ?? record.code ?? record.field ?? record.id ?? ""),
      );
      name = String(record.name ?? record.label ?? record.display_name ?? "");
    }
    if (!code || seen.has(code)) continue;
    seen.add(code);
    targets.push({
      code,
      name: labelForCode(code, name),
      field: `${COUNT_FIELD_PREFIX}${code}`,
    });
  }

  return { targets, operators: readOperators(source) };
}

export async function fetchConditionalCountOptions(): Promise<ConditionalCountOptions> {
  const result = await apiClient.get<unknown>(
    "/auto-tagging/conditional-plans/count-options",
  );
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Failed to load count options.");
  }
  const options = normalizeConditionalCountOptions(result.data ?? result);
  rememberCountTargetLabels(options.targets);
  return options;
}
